/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2025 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import React, { FC } from "react";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { AppLogo } from "@advapacs/common";
import { IRootState } from "@src/config/reducers";
import { useAppDispatch } from "@src/config/store";
import { markAdvapacsViewerSeen } from "@src/pages/login/ducks/operations";
import Banner from "../../assets/images/viewer/Banner.png";
import SparkleIcon from "../../assets/images/viewer/AI.svg";
import LinkIcon from "../../assets/images/viewer/Link.svg";
import FlashIcon from "../../assets/images/viewer/Flash.svg";
import GroupIcon from "../../assets/images/viewer/Group.svg";

const AdvaViewIntroModal: FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { account } = useSelector((state: IRootState) => state.loginReducer);
  const show = !account?.advaViewOnly && account?.showIntroduceAdvaView;

  const handleClose = () => {
    dispatch(markAdvapacsViewerSeen());
  };

  const features: { icon: string; className: string; label: string }[] = [
    {
      icon: LinkIcon,
      className: "advaview-intro-icon-link",
      label: t("advaview_intro.feature_integrated"),
    },
    {
      icon: FlashIcon,
      className: "advaview-intro-icon-flash",
      label: t("advaview_intro.feature_faster_access"),
    },
    {
      icon: "",
      className: "advaview-intro-icon-navigation",
      label: t("advaview_intro.feature_navigation"),
    },
    {
      icon: GroupIcon,
      className: "advaview-intro-icon-group",
      label: t("advaview_intro.feature_consistent_workflow"),
    },
  ];

  if (!show) return null;

  return (
    <Modal
      size="xl"
      centered
      className="advaview-intro-modal"
      show={show}
      onHide={handleClose}
      backdrop={false}
    >
      <Modal.Body>
        <div className="advaview-intro-body">
          <div className="advaview-intro-media">
            <img src={Banner} alt="AdvaView preview" loading="lazy" />
          </div>
          <div className="advaview-intro-content">
            <span className="advaview-intro-badge">
              <img src={SparkleIcon} alt="" aria-hidden="true" />
              {t("advaview_intro.badge")}
            </span>
            <h2 className="advaview-intro-title">
              {t("advaview_intro.title_prefix")}{" "}
              <span className="advaview-intro-brand">AdvaView</span>
            </h2>
            <p className="advaview-intro-subtitle">
              {t("advaview_intro.subtitle")}
            </p>
            <p className="advaview-intro-description">
              {t("advaview_intro.description")}
            </p>
            <ul className="advaview-intro-features list-unstyled">
              {features.map((feature, index) => (
                <li key={feature.label} className="d-flex align-items-center">
                  <span
                    className={`advaview-intro-feature-icon ${feature.className}`}
                  >
                    {feature.icon ? (
                      <img
                        src={feature.icon}
                        alt=""
                        className={`${
                          index === features.length - 1 ? "last" : ""
                        } `}
                        aria-hidden="true"
                      />
                    ) : (
                      <i className="fa fa-desktop" aria-hidden="true" />
                    )}
                  </span>
                  <span>{feature.label}</span>
                </li>
              ))}
            </ul>
            <div className="advaview-intro-tip d-flex align-items-center ">
              <span className="m-r-xs">{t("advaview_intro.tip")}</span>
              <AppLogo.LogoShort />
            </div>
            <p className="advaview-intro-footnote">
              {t("advaview_intro.footer_note")}
            </p>
            <button
              type="button"
              className="btn btn-secondary btn-fixed btn-advapacs-viewer-intro"
              style={{ width: 110 }}
              onClick={handleClose}
            >
              {t("close")}
            </button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default AdvaViewIntroModal;
